import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
	BarChart3,
	CreditCard,
	Package,
	Receipt,
	RotateCcw,
	ShoppingCart,
	Tags,
	TrendingUp,
	Trophy,
} from "lucide-react";
import { reportsAPI } from "@/lib/api";
import { useCurrency } from "@/hooks/useCurrency";
import { formatDate, formatNumber } from "@/lib/utils";
import { ReportTable } from "../components/ReportTable";
import { RankedBars } from "../components/RankedBars";
import { StatTile } from "../components/StatTile";
import { TrendChart, type TrendPoint } from "../components/TrendChart";
import {
	Panel,
	ReportNote,
	ReportState,
	Segmented,
	SplitRow,
	StatGrid,
	StatusBadge,
	useReportQuery,
} from "../components/ReportBlocks";
import { num, personName, share, type ReportColumn } from "../lib/columns";
import { paymentMethodLabel, statusLabel } from "../lib/labels";
import { getPreviousRange, parseYmd, toYmd, type ReportBodyProps, type ReportFiltersParams } from "../types";

/** Rows the transaction endpoints return at most; past this the table asks for a narrower period. */
export const ROW_CAP = 1000;

type GroupBy = "day" | "week" | "month";

interface TrendRow {
	date: string;
	totalSales: number;
	totalRevenue: number;
	totalItems: number;
}

const sumBy = <T,>(rows: T[], pick: (row: T) => number) => rows.reduce((s, r) => s + pick(r), 0);

/** Day buckets up to ~6 weeks, weeks up to ~6 months, months beyond (or for "all time"). */
function autoGroupBy(params: ReportFiltersParams): GroupBy {
	const start = parseYmd(params.startDate);
	const end = parseYmd(params.endDate);
	if (!start || !end) return "month";
	const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
	return days <= 45 ? "day" : days <= 200 ? "week" : "month";
}

function bucketStart(date: Date, groupBy: GroupBy): Date {
	if (groupBy === "month") return new Date(date.getFullYear(), date.getMonth(), 1);
	if (groupBy === "week") {
		// Postgres DATE_TRUNC('week') starts weeks on Monday.
		const offset = (date.getDay() + 6) % 7;
		return new Date(date.getFullYear(), date.getMonth(), date.getDate() - offset);
	}
	return date;
}

function nextBucket(date: Date, groupBy: GroupBy): Date {
	if (groupBy === "month") return new Date(date.getFullYear(), date.getMonth() + 1, 1);
	return new Date(date.getFullYear(), date.getMonth(), date.getDate() + (groupBy === "week" ? 7 : 1));
}

/** Most buckets worth zero-filling; past this (e.g. "all time" by day) the rows are shown as returned. */
const MAX_FILLED_BUCKETS = 400;

/** Every bucket of the period, with zeros where nothing was sold, so the line never skips a quiet day. */
export function fillBuckets(rows: TrendRow[], groupBy: GroupBy, params: ReportFiltersParams): TrendRow[] {
	const byDate = new Map(rows.map(r => [String(r.date).slice(0, 10), r]));
	const first = parseYmd(params.startDate) ?? parseYmd(String(rows[0]?.date ?? "").slice(0, 10));
	const last = parseYmd(params.endDate) ?? parseYmd(String(rows[rows.length - 1]?.date ?? "").slice(0, 10));
	if (!first || !last) return rows;
	const out: TrendRow[] = [];
	for (let d = bucketStart(first, groupBy); d <= last; d = nextBucket(d, groupBy)) {
		// Never drop data: a range too long to fill keeps only the buckets that have sales.
		if (out.length >= MAX_FILLED_BUCKETS) return rows;
		const key = toYmd(d);
		out.push(byDate.get(key) ?? { date: key, totalSales: 0, totalRevenue: 0, totalItems: 0 });
	}
	return out;
}

function bucketLabel(t: TFunction, date: string, groupBy: GroupBy): { label: string; title: string } {
	const d = parseYmd(String(date).slice(0, 10));
	if (!d) return { label: date, title: date };
	const full = formatDate(d);
	if (groupBy === "month") {
		const month = full.slice(3);
		return { label: month, title: month };
	}
	const short = full.slice(0, 5);
	return groupBy === "week"
		? { label: short, title: t("reports.salesTrend.weekOf", { date: full }) }
		: { label: short, title: full };
}

/* ─────────────────────────────── Sales trend ─────────────────────────────── */

export function SalesTrendReport({ params, filters, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const [override, setOverride] = useState<GroupBy | null>(null);
	const groupBy = override ?? autoGroupBy(params);
	const previous = getPreviousRange(filters);

	const query = useReportQuery(
		"sales-trend",
		{ params, groupBy },
		() => reportsAPI.getSalesByDateRange({ ...params, groupBy }) as Promise<TrendRow[]>,
	);
	const previousQuery = useReportQuery(
		"sales-trend-previous",
		{ params, previous },
		() =>
			reportsAPI.getSalesByDateRange({
				...params,
				startDate: previous?.startDate,
				endDate: previous?.endDate,
				groupBy: "month",
			}) as Promise<TrendRow[]>,
		Boolean(previous),
	);
	const prev =
		previous && previousQuery.isSuccess
			? {
					revenue: sumBy(previousQuery.data ?? [], r => num(r.totalRevenue)),
					invoices: sumBy(previousQuery.data ?? [], r => num(r.totalSales)),
				}
			: undefined;

	return (
		<ReportState query={query}>
			{raw => {
				const apiRows = Array.isArray(raw) ? raw : [];
				const rows = fillBuckets(apiRows, groupBy, params);
				// Totals come from what the API returned, never from the (possibly capped) chart buckets.
				const revenue = sumBy(apiRows, r => num(r.totalRevenue));
				const invoices = sumBy(apiRows, r => num(r.totalSales));
				const best = apiRows.reduce<TrendRow | null>(
					(b, r) => (!b || num(r.totalRevenue) > num(b.totalRevenue) ? r : b),
					null,
				);
				const hasBest = Boolean(best && num(best.totalRevenue) > 0);
				const points: TrendPoint[] = rows.map(r => ({
					...bucketLabel(t, r.date, groupBy),
					value: num(r.totalRevenue),
					detail: t("reports.salesTrend.invoicesCount", { count: num(r.totalSales) }),
				}));
				const columns: ReportColumn<TrendRow>[] = [
					{
						key: "date",
						label: t(`reports.salesTrend.bucket.${groupBy}`),
						value: r => r.date,
						render: r => <span dir="ltr">{bucketLabel(t, r.date, groupBy).title}</span>,
					},
					{
						key: "invoices",
						label: t("reports.cols.invoices"),
						value: r => num(r.totalSales),
						kind: "number",
						total: true,
					},
					{
						key: "items",
						label: t("reports.cols.itemsSold"),
						value: r => num(r.totalItems),
						kind: "number",
						total: true,
						hideOnMobile: true,
					},
					{
						key: "avg",
						label: t("reports.cols.avgInvoice"),
						value: r => (num(r.totalSales) ? num(r.totalRevenue) / num(r.totalSales) : 0),
						kind: "money",
						currency,
						hideOnMobile: true,
					},
					{
						key: "revenue",
						label: t("reports.cols.revenue"),
						value: r => num(r.totalRevenue),
						kind: "money",
						currency,
						total: true,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.sales")}
								value={revenue}
								format="money"
								currency={currency}
								previous={prev?.revenue}
							/>
							<StatTile
								icon={Receipt}
								label={t("reports.kpi.invoices")}
								value={invoices}
								previous={prev?.invoices}
							/>
							<StatTile
								icon={BarChart3}
								label={t("reports.kpi.avgInvoice")}
								value={invoices ? revenue / invoices : 0}
								format="money"
								currency={currency}
								previous={prev ? (prev.invoices ? prev.revenue / prev.invoices : 0) : undefined}
							/>
							<StatTile
								icon={Trophy}
								label={t(`reports.salesTrend.best.${groupBy}`)}
								value={hasBest ? num(best!.totalRevenue) : null}
								format="money"
								currency={currency}
								hint={hasBest ? bucketLabel(t, best!.date, groupBy).title : undefined}
							/>
						</StatGrid>
						<Panel
							title={t("reports.salesTrend.chartTitle")}
							actions={
								<Segmented
									label={t("reports.salesTrend.groupBy")}
									value={groupBy}
									onChange={setOverride}
									options={(["day", "week", "month"] as GroupBy[]).map(g => ({
										value: g,
										label: t(`reports.salesTrend.by.${g}`),
									}))}
								/>
							}
						>
							{revenue > 0 ? (
								<TrendChart
									data={points}
									ariaLabel={t("reports.salesTrend.chartTitle")}
									currency={currency}
								/>
							) : (
								<p className="py-16 text-center text-sm text-muted-foreground">
									{t("reports.empty.title")}
								</p>
							)}
						</Panel>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={r => r.date}
							exportName={exportName}
							emptyIcon={TrendingUp}
							defaultSort={{ key: "date", dir: "desc" }}
							searchable={false}
						/>
						<ReportNote>{t("reports.notes.paidSales")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Sales invoices ─────────────────────────────── */

export function salesTransactionColumns(t: TFunction, currency: string): ReportColumn<any>[] {
	return [
		{
			key: "invoice",
			label: t("reports.cols.invoice"),
			value: s => s.invoiceNumber || s.reference || `#${s.id}`,
		},
		{ key: "date", label: t("reports.cols.dateTime"), value: s => s.createdAt, kind: "datetime" },
		{
			key: "customer",
			label: t("reports.cols.customer"),
			value: s => personName(s.customer) || t("reports.walkIn"),
		},
		{ key: "cashier", label: t("reports.cols.cashier"), value: s => personName(s.createdBy), hideOnMobile: true },
		{ key: "branch", label: t("reports.cols.branch"), value: s => s.branch?.name ?? "", hideOnMobile: true },
		{
			key: "method",
			label: t("reports.cols.paymentMethod"),
			value: s => paymentMethodLabel(t, s.paymentMethod),
			hideOnMobile: true,
		},
		{
			key: "status",
			label: t("reports.cols.status"),
			value: s => statusLabel(t, "sales.statuses", s.status),
			render: s => <StatusBadge status={s.status} label={statusLabel(t, "sales.statuses", s.status)} />,
			align: "center",
		},
		{
			key: "payment",
			label: t("reports.cols.paymentStatus"),
			value: s => statusLabel(t, "sales.paymentStatuses", s.paymentStatus),
			render: s => (
				<StatusBadge
					status={s.paymentStatus}
					label={statusLabel(t, "sales.paymentStatuses", s.paymentStatus)}
				/>
			),
			align: "center",
			hideOnMobile: true,
		},
		{
			key: "tax",
			label: t("reports.cols.tax"),
			value: s => num(s.taxAmount),
			kind: "money",
			currency,
			total: true,
			hideOnMobile: true,
		},
		{
			key: "total",
			label: t("reports.cols.total"),
			value: s => num(s.totalAmount),
			kind: "money",
			currency,
			total: true,
		},
	];
}

export const loadSalesTransactions = (params: ReportFiltersParams) =>
	reportsAPI.getSalesTransactions({ ...params, page: 1, limit: ROW_CAP }) as Promise<{
		sales: any[];
		total: number;
	}>;

export function SalesTransactionsReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const list = useReportQuery("sales-transactions", params, () => loadSalesTransactions(params));
	const summary = useReportQuery("sales-transactions-summary", params, async () => {
		const [sales, revenue] = await Promise.all([
			reportsAPI.getSalesSummary(params),
			reportsAPI.getRevenueSummary(params),
		]);
		return { sales, revenue };
	});
	const columns = useMemo(() => salesTransactionColumns(t, currency), [t, currency]);

	return (
		<ReportState query={list}>
			{data => {
				const rows = data?.sales ?? [];
				const s = summary.data;
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.paidSales")}
								value={num(s?.revenue?.totalRevenue)}
								format="money"
								currency={currency}
								loading={summary.isPending}
								hint={t("reports.kpi.paidSalesHint", { count: num(s?.sales?.totalSales) })}
							/>
							<StatTile
								icon={Receipt}
								label={t("reports.kpi.allInvoices")}
								value={num(data?.total ?? rows.length)}
								loading={summary.isPending}
								hint={t("reports.kpi.cancelledHint", { count: num(s?.sales?.cancelledSales) })}
							/>
							<StatTile
								icon={Receipt}
								label={t("reports.kpi.vatCollected")}
								value={num(s?.revenue?.totalTax)}
								format="money"
								currency={currency}
								loading={summary.isPending}
							/>
							<StatTile
								icon={Tags}
								label={t("reports.kpi.discounts")}
								value={num(s?.revenue?.totalDiscount)}
								format="money"
								currency={currency}
								loading={summary.isPending}
							/>
						</StatGrid>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={s => s.id}
							rowHref={s => `/sales/${s.id}`}
							exportName={exportName}
							emptyIcon={ShoppingCart}
							truncated={{ shown: rows.length, total: num(data?.total) }}
							defaultSort={{ key: "date", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.invoicesAllStatuses")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Product sales ─────────────────────────────── */

export function productSalesColumns(t: TFunction, currency: string, totalRevenue: number): ReportColumn<any>[] {
	return [
		{ key: "product", label: t("reports.cols.product"), value: p => p.productName },
		{
			key: "barcode",
			label: t("products.table.barcode"),
			value: p => p.productBarcode || "",
			hideOnMobile: true,
		},
		{ key: "category", label: t("reports.cols.category"), value: p => p.categoryName || "", hideOnMobile: true },
		{
			key: "invoices",
			label: t("reports.cols.invoices"),
			value: p => num(p.totalSales),
			kind: "number",
			hideOnMobile: true,
		},
		{ key: "qty", label: t("reports.cols.qtySold"), value: p => num(p.totalQuantity), kind: "number", total: true },
		{
			key: "avg",
			label: t("reports.cols.avgPrice"),
			value: p => num(p.averagePrice),
			kind: "money",
			currency,
			hideOnMobile: true,
		},
		{
			key: "revenue",
			label: t("reports.cols.revenue"),
			value: p => num(p.totalRevenue),
			kind: "money",
			currency,
			total: true,
		},
		{
			key: "share",
			label: t("reports.cols.share"),
			value: p => share(num(p.totalRevenue), totalRevenue),
			kind: "percent",
		},
	];
}

export const loadProductSales = (params: ReportFiltersParams) =>
	reportsAPI
		.getProductsReport(params)
		.then((d: any) => (Array.isArray(d) ? d : (d?.products ?? d?.data ?? [])) as any[]);

export function ProductSalesReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const query = useReportQuery("product-sales", params, () => loadProductSales(params));
	return (
		<ReportState query={query}>
			{rows => {
				const revenue = sumBy(rows, p => num(p.totalRevenue));
				const units = sumBy(rows, p => num(p.totalQuantity));
				const top = [...rows].sort((a, b) => num(b.totalRevenue) - num(a.totalRevenue))[0];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.productRevenue")}
								value={revenue}
								format="money"
								currency={currency}
							/>
							<StatTile icon={Package} label={t("reports.kpi.unitsSold")} value={units} />
							<StatTile icon={Tags} label={t("reports.kpi.productsSold")} value={rows.length} />
							<StatTile
								icon={Trophy}
								label={t("reports.kpi.bestSeller")}
								value={top ? num(top.totalRevenue) : null}
								format="money"
								currency={currency}
								hint={top?.productName}
							/>
						</StatGrid>
						<SplitRow>
							<RankedBars
								title={t("reports.charts.topByRevenue")}
								currency={currency}
								showShare
								items={rows.map(p => ({
									key: p.productId,
									label: p.productName,
									value: num(p.totalRevenue),
									href: `/products/${p.productId}`,
								}))}
							/>
							<RankedBars
								title={t("reports.charts.topByQuantity")}
								format="number"
								items={rows.map(p => ({
									key: p.productId,
									label: p.productName,
									value: num(p.totalQuantity),
									href: `/products/${p.productId}`,
								}))}
							/>
						</SplitRow>
						<ReportTable
							columns={productSalesColumns(t, currency, revenue)}
							rows={rows}
							rowKey={p => p.productId}
							rowHref={p => `/products/${p.productId}`}
							exportName={exportName}
							emptyIcon={Package}
						/>
						<ReportNote>{t("reports.notes.paidSales")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Sales by category ─────────────────────────────── */

export function CategoriesReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const query = useReportQuery(
		"categories",
		params,
		() => reportsAPI.getCategoriesReport(params) as Promise<{ categories?: any[] }>,
	);
	return (
		<ReportState query={query}>
			{data => {
				const rows = data?.categories ?? [];
				const revenue = sumBy(rows, c => num(c.salesRevenue));
				const stock = sumBy(rows, c => num(c.stockValue));
				const top = [...rows].sort((a, b) => num(b.salesRevenue) - num(a.salesRevenue))[0];
				const hasTop = Boolean(top && num(top.salesRevenue) > 0);
				const columns: ReportColumn<any>[] = [
					{ key: "name", label: t("reports.cols.category"), value: c => c.name },
					{
						key: "revenue",
						label: t("reports.cols.revenue"),
						value: c => num(c.salesRevenue),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "share",
						label: t("reports.cols.share"),
						value: c => share(num(c.salesRevenue), revenue),
						kind: "percent",
					},
					{
						key: "stock",
						label: t("reports.cols.stockValueCost"),
						value: c => num(c.stockValue),
						kind: "money",
						currency,
						total: true,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={TrendingUp}
								label={t("reports.kpi.categoryRevenue")}
								value={revenue}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={Tags}
								label={t("reports.kpi.categoriesWithSales")}
								value={rows.filter(c => num(c.salesRevenue) > 0).length}
								hint={t("reports.kpi.ofTotal", { count: rows.length })}
							/>
							<StatTile
								icon={Trophy}
								label={t("reports.kpi.topCategory")}
								value={hasTop ? num(top.salesRevenue) : null}
								format="money"
								currency={currency}
								hint={hasTop ? top.name : undefined}
							/>
							<StatTile
								icon={Package}
								label={t("reports.kpi.stockValueCost")}
								value={stock}
								format="money"
								currency={currency}
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.revenueByCategory")}
							currency={currency}
							showShare
							items={rows.map(c => ({ key: c.id, label: c.name, value: num(c.salesRevenue) }))}
						/>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={c => c.id}
							exportName={exportName}
							emptyIcon={Tags}
						/>
						<ReportNote>{t("reports.notes.categories")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Payment methods ─────────────────────────────── */

export function paymentMethodColumns(t: TFunction, currency: string, total: number): ReportColumn<any>[] {
	return [
		{ key: "method", label: t("reports.cols.paymentMethod"), value: r => paymentMethodLabel(t, r.paymentMethod) },
		{ key: "count", label: t("reports.cols.transactions"), value: r => num(r.count), kind: "number", total: true },
		{
			key: "avg",
			label: t("reports.cols.avgTicket"),
			value: r => (num(r.count) ? num(r.total) / num(r.count) : 0),
			kind: "money",
			currency,
			hideOnMobile: true,
		},
		{
			key: "total",
			label: t("reports.cols.total"),
			value: r => num(r.total),
			kind: "money",
			currency,
			total: true,
		},
		{ key: "share", label: t("reports.cols.share"), value: r => share(num(r.total), total), kind: "percent" },
	];
}

export const loadPaymentMethods = (params: ReportFiltersParams) =>
	reportsAPI.getPaymentMethodsReport(params).then((d: any) => (d?.byMethod ?? []) as any[]);

export function PaymentMethodsReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const query = useReportQuery("payment-methods", params, () => loadPaymentMethods(params));
	return (
		<ReportState query={query}>
			{rows => {
				const total = sumBy(rows, r => num(r.total));
				const count = sumBy(rows, r => num(r.count));
				const top = [...rows].sort((a, b) => num(b.total) - num(a.total))[0];
				const cash = rows.filter(r => String(r.paymentMethod).toLowerCase() === "cash");
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={CreditCard}
								label={t("reports.kpi.collected")}
								value={total}
								format="money"
								currency={currency}
							/>
							<StatTile icon={Receipt} label={t("reports.kpi.transactions")} value={count} />
							<StatTile
								icon={Trophy}
								label={t("reports.kpi.topMethod")}
								value={top ? share(num(top.total), total) : null}
								format="percent"
								hint={top ? paymentMethodLabel(t, top.paymentMethod) : undefined}
							/>
							<StatTile
								icon={CreditCard}
								label={t("reports.kpi.cashShare")}
								value={share(
									sumBy(cash, r => num(r.total)),
									total,
								)}
								format="percent"
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.shareByMethod")}
							currency={currency}
							showShare
							items={rows.map(r => ({
								key: r.paymentMethod,
								label: paymentMethodLabel(t, r.paymentMethod),
								value: num(r.total),
								sub: t("reports.salesTrend.invoicesCount", { count: num(r.count) }),
							}))}
						/>
						<ReportTable
							columns={paymentMethodColumns(t, currency, total)}
							rows={rows}
							rowKey={r => r.paymentMethod}
							exportName={exportName}
							emptyIcon={CreditCard}
							searchable={false}
						/>
						<ReportNote>{t("reports.notes.paymentMethods")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── Returns ─────────────────────────────── */

export function returnColumns(t: TFunction, currency: string): ReportColumn<any>[] {
	const isSupplier = (r: any) => r.type === "PURCHASE_RETURN";
	return [
		{ key: "number", label: t("reports.cols.returnNumber"), value: r => r.returnNumber || `#${r.id}` },
		{
			key: "type",
			label: t("reports.cols.type"),
			value: r => (isSupplier(r) ? t("returns.supplierReturn") : t("returns.customerReturn")),
		},
		{ key: "date", label: t("reports.cols.date"), value: r => r.returnDate || r.createdAt, kind: "date" },
		{
			key: "party",
			label: t("reports.cols.party"),
			value: r => r.supplier?.name || (isSupplier(r) ? "" : t("reports.walkIn")),
			hideOnMobile: true,
		},
		{ key: "reason", label: t("reports.cols.reason"), value: r => r.reason || "", hideOnMobile: true },
		{
			key: "status",
			label: t("reports.cols.status"),
			value: r => statusLabel(t, "returns.statuses", r.status),
			render: r => (
				<StatusBadge
					status={String(r.status ?? "").toLowerCase()}
					label={statusLabel(t, "returns.statuses", r.status)}
				/>
			),
			align: "center",
		},
		{
			key: "total",
			label: t("reports.cols.total"),
			value: r => num(r.totalAmount),
			kind: "money",
			currency,
			total: true,
		},
	];
}

export const loadReturns = (params: ReportFiltersParams) =>
	reportsAPI.getReturnsTransactions({ ...params, page: 1, limit: ROW_CAP }) as Promise<{
		returns: any[];
		total: number;
	}>;

export function ReturnsReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const list = useReportQuery("returns", params, () => loadReturns(params));
	const summary = useReportQuery("returns-summary", params, () => reportsAPI.getReturnsSummaryReport(params));
	const columns = useMemo(() => returnColumns(t, currency), [t, currency]);
	return (
		<ReportState query={list}>
			{data => {
				const rows = data?.returns ?? [];
				const s: any = summary.data;
				const byProduct: any[] = Array.isArray(s?.byProduct) ? s.byProduct : [];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={RotateCcw}
								label={t("reports.kpi.processedReturns")}
								value={num(s?.totalReturns)}
								loading={summary.isPending}
							/>
							<StatTile
								icon={RotateCcw}
								label={t("reports.kpi.returnedAmount")}
								value={num(s?.totalReturnAmount)}
								format="money"
								currency={currency}
								loading={summary.isPending}
								tone="negative"
							/>
							<StatTile
								icon={BarChart3}
								label={t("reports.kpi.returnRate")}
								value={num(s?.returnRate)}
								format="percent"
								loading={summary.isPending}
								hint={t("reports.kpi.returnRateHint")}
							/>
							<StatTile
								icon={Receipt}
								label={t("reports.kpi.allReturns")}
								value={num(data?.total ?? rows.length)}
							/>
						</StatGrid>
						{byProduct.length > 0 && (
							<RankedBars
								title={t("reports.charts.mostReturned")}
								currency={currency}
								limit={5}
								items={byProduct.map(p => ({
									key: p.productId,
									label: p.productName,
									value: num(p.amount),
									sub: t("reports.charts.units", { formatted: formatNumber(num(p.quantity)) }),
								}))}
							/>
						)}
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={r => r.id}
							rowHref={r => `/returns/${r.id}`}
							exportName={exportName}
							emptyIcon={RotateCcw}
							truncated={{ shown: rows.length, total: num(data?.total) }}
							defaultSort={{ key: "date", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.returns")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}
